import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { getPuzzleType } from "@/puzzle-types/registry";
import { RulesText } from "@/core/puzzles/PuzzlePreviewPage";
import type { PuzzleCredit } from "@/core/puzzles/api";
import type { SheetOptions } from "./options";

/**
 * The encoder ships with the sheet, not with the application: a player who never prints has
 * no use for it, as they have no use for a type they never open (CLAUDE.md §7.4).
 */
const QrCode = lazy(() => import("./QrCode"));

/** Everything the sheet says about a puzzle. Assembled by the page; the document only draws. */
export interface SheetSource {
  slug: string;
  puzzleType: string;
  title: string;
  author: string;
  difficulty: number;
  publishedAt: string | null;
  credit: PuzzleCredit | null;
  definition: unknown;
  rules: string;
  /** Where the solver goes to enter the answer; the sheet prints it at the foot of each page. */
  address: string;
  /** The same place, absolute, for the QR code: a camera cannot guess a scheme. */
  url: string;
  /** Who publishes the sheet, for the same line. */
  publisher: string;
}

/**
 * The PDF sheet: a paged document, laid out on screen exactly as it prints.
 *
 * The core owns the page — heading, facts, rules, credit, footer — and the type draws the
 * puzzle through its `PrintView`, as it draws it on screen through its `Player`. Each page is
 * one physical sheet: `@page` has no margin and the page carries its own, so what the
 * screen shows is what the PDF contains, page for page.
 */
export function PuzzleSheet({ source, options }: { source: SheetSource; options: SheetOptions }) {
  const pages = options.secondCopy ? [options.rules, false] : [options.rules];
  return (
    <div className="bt-sheet" data-paper={options.paper} data-testid="sheet">
      <style>{`@page { size: ${options.paper === "letter" ? "letter" : "A4"}; margin: 0; }`}</style>
      {pages.map((withRules, index) => (
        <SheetPage
          key={index}
          source={source}
          withRules={withRules}
          withQr={options.qr}
          number={index + 1}
          count={pages.length}
        />
      ))}
    </div>
  );
}

function SheetPage({
  source,
  withRules,
  withQr,
  number,
  count,
}: {
  source: SheetSource;
  withRules: boolean;
  withQr: boolean;
  number: number;
  count: number;
}) {
  const { t, i18n } = useTranslation();
  const View = getPuzzleType(source.puzzleType)?.PrintView;
  const published = source.publishedAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(
        new Date(source.publishedAt),
      )
    : null;
  return (
    <article
      className={withRules ? "bt-sheet__page bt-sheet__page--with-rules" : "bt-sheet__page"}
      aria-label={t("sheet.page", { number, count })}
    >
      <header className="bt-sheet__header">
        <div className="bt-sheet__heading">
          <h1 className="bt-sheet__title">{source.title}</h1>
          <p className="bt-sheet__author">{t("puzzles.by", { author: source.author })}</p>
        </div>
        <div className="bt-sheet__facts">
          <Difficulty value={source.difficulty} />
          {published ? <p className="bt-sheet__figure">{published}</p> : null}
        </div>
      </header>
      <div className="bt-sheet__puzzle">
        {View ? (
          <Suspense fallback={<p>{t("app.loading")}</p>}>
            <View definition={source.definition} />
          </Suspense>
        ) : null}
      </div>
      {withRules && source.rules ? (
        <section className="bt-sheet__rules" aria-label={t("play.rules")}>
          <h2 className="bt-sheet__rules-title">{t("play.rules")}</h2>
          <div className="bt-sheet__rules-body">
            <RulesText text={source.rules} paper />
          </div>
        </section>
      ) : null}
      <footer className="bt-sheet__footer">
        <div className="bt-sheet__imprint">
          <Credit credit={source.credit} />
          <p className="bt-sheet__origin">
            <span>{source.publisher}</span>
            <span className="bt-sheet__figure">{source.address}</span>
          </p>
        </div>
        {withQr ? <Scan source={source} /> : null}
      </footer>
    </article>
  );
}

function Difficulty({ value }: { value: number }) {
  const { t } = useTranslation();
  const filled = Math.max(0, Math.min(5, value));
  return (
    <p className="bt-sheet__difficulty" aria-label={t("puzzles.difficulty", { value })}>
      {"★".repeat(filled)}
      <span className="bt-sheet__difficulty-empty">{"★".repeat(5 - filled)}</span>
    </p>
  );
}

/**
 * The printed address made actionable: the solver fills the grid in pencil, then scans to
 * come back and submit. It sits at the foot beside the address it stands for — the top of
 * the page belongs to the puzzle, and this is what you reach for once you are done with it.
 */
function Scan({ source }: { source: SheetSource }) {
  const { t } = useTranslation();
  return (
    <div className="bt-sheet__scan">
      {/* The box is held whatever the chunk is doing, so the page does not reflow around it. */}
      <Suspense fallback={<span className="bt-sheet__qr-code" />}>
        <QrCode url={source.url} label={t("sheet.qr_alt", { address: source.address })} />
      </Suspense>
      <p className="bt-sheet__scan-caption">{t("sheet.qr_caption")}</p>
    </div>
  );
}

/** On paper a link is only its address, so the credit spells the address out. */
function Credit({ credit }: { credit: PuzzleCredit | null }) {
  const { t } = useTranslation();
  if (!credit || (!credit.author && !credit.url)) return null;
  return (
    <p className="bt-sheet__credit">
      <span>{t("puzzles.source")}</span> <span>{credit.author}</span>
      {credit.url ? <span className="bt-sheet__figure">{credit.url}</span> : null}
    </p>
  );
}
